type Attrs = Record<string, string | boolean | undefined>;
type Child = Node | string | null | undefined | false;

/** Minimal element builder. Text goes in via textContent, so nothing here can inject HTML. */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: Child[] = [],
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  for (const [name, value] of Object.entries(attrs)) {
    if (value === undefined || value === false) continue;
    element.setAttribute(name, value === true ? "" : value);
  }
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    element.append(typeof child === "string" ? document.createTextNode(child) : child);
  }
  return element;
}

export function on<K extends keyof HTMLElementEventMap>(
  element: Element,
  event: K,
  handler: (event: HTMLElementEventMap[K]) => void,
): void {
  element.addEventListener(event, handler as EventListener);
}

/**
 * A shadow root keeps host CSS out and our CSS in — the difference between a banner that
 * looks the same on every site and one that breaks on the first customer with an
 * opinionated stylesheet. Sites that want to restyle it can opt out via `shadowDom: false`.
 */
export function createRoot(shadow: boolean): { host: HTMLElement; root: ShadowRoot | HTMLElement } {
  const host = document.createElement("div");
  host.className = "consentkit-host";
  host.style.cssText = "position:relative;z-index:2147483640";

  const canUseShadow = shadow && typeof host.attachShadow === "function";
  const root = canUseShadow ? host.attachShadow({ mode: "open" }) : host;
  (document.body || document.documentElement).appendChild(host);
  return { host, root };
}

export function injectStyles(root: ShadowRoot | HTMLElement, css: string): void {
  const style = document.createElement("style");
  style.textContent = css;
  root.appendChild(style);
}

/** RTL comes from the host document, so an Arabic page gets a mirrored banner for free. */
export function documentDirection(): "ltr" | "rtl" {
  const dir = document.documentElement.getAttribute("dir");
  return dir === "rtl" ? "rtl" : "ltr";
}
